import { createFileRoute, notFound } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import GuidePage from '@/pages/GuidePage';
import { pageHead } from '@/lib/routeHead';
import { guideQueryOptions } from '@/lib/guideQueries';

/**
 * Rendered when the server couldn't see the guide (unpublished drafts are
 * invisible to anonymous reads). The guide query itself falls back to an
 * authenticated read in the browser, so signed-in admins still get their
 * draft here; everyone else keeps a real 404.
 */
function GuideDraftRetry() {
  const { slug } = Route.useParams();
  const { data: guide } = useQuery({
    ...guideQueryOptions(slug),
    refetchOnMount: 'always',
    staleTime: 0,
  });
  if (guide) return <GuidePage />;
  return <div className="container mx-auto px-4 py-24 text-center text-muted-foreground">Guide not found.</div>;
}

export const Route = createFileRoute('/guide/$slug')({
  errorComponent: () => <div role="alert">The guide could not be loaded. Please try again.</div>,
  notFoundComponent: () => <GuideDraftRetry />,
  loader: async ({ params, context }) => {
    const [guide, { guideCover, guideImage, guideImageSrcSet }] = await Promise.all([
      context.queryClient.ensureQueryData(guideQueryOptions(params.slug)),
      import('@/lib/guideImages'),
    ]);
    if (!guide) throw notFound();
    const image = guideImage(guide.hero_image);
    return { ...guide, coverPreload: guide.hero_image ? {
      rel: 'preload', as: 'image', href: guideCover(guide.hero_image),
      imageSrcSet: image ? guideImageSrcSet(image).split(', ').filter(candidate => !candidate.endsWith(' 400w')).join(', ') : undefined,
      imageSizes: '100vw', fetchPriority: 'high' as const,
    } : null };
  },
  head: ({ loaderData: guide, params }) => {
    const head = pageHead({
      title: guide?.title ?? "RV Buyer's Guide",
      description: guide?.meta_description || guide?.html_content.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 170) || 'RV buying advice from Rex & Sons RVs in Castle Hayne, NC.',
      path: `/guide/${params.slug}`, type: 'article',
      ...(guide?.hero_image?.startsWith('https://') ? { image: guide.hero_image } : {}),
    });
    return { ...head,
      links: [...head.links, ...(guide?.coverPreload ? [guide.coverPreload] : [])],
      scripts: guide?.schema_json ? [{ type: 'application/ld+json', children: JSON.stringify(guide.schema_json) }] : [],
    };
  }, component: GuidePage,
});
